import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Modal, ModalBody } from "react-bootstrap";
import { useForm, useFieldArray } from "react-hook-form";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { confirm } from "../../common/ConfirmDialog";
import { triggerAlert } from "../../utils";

/**
 * Calling V2 → Tool Catalog.
 *
 * HTTP tools (catalog rows with owner_org=NULL) any V2 agent template can
 * reference. Each row is a single endpoint the runtime POSTs to during a
 * call. Customers see this catalog when picking tools for their own agents.
 */
export default function CallingV2Tools() {
  document.title = "Digital Employees — V2 Tool Catalog";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [showTest, setShowTest] = useState(null); // { tool, result, loading }

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm({
    defaultValues: { headers: [], schema_fields: [] },
  });
  const { fields: headerFields, append: appendHeader, remove: removeHeader } = useFieldArray({ control, name: "headers" });
  const { fields: schemaFields, append: appendSchema, remove: removeSchema } = useFieldArray({ control, name: "schema_fields" });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await CallingV2.listTools();
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load tools.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Dict ⇆ rows helpers. The backend stores headers as a flat string→string
  // dict and input_schema as a JSON-Schema object; admins enter rows in the
  // UI, we convert at the boundary.
  const headersToRows = (headers) =>
    Object.entries(headers || {}).map(([key, value]) => ({ key, value: String(value ?? "") }));

  const rowsToHeaders = (rows) => {
    const out = {};
    for (const r of rows || []) {
      const k = (r?.key || "").trim();
      if (!k) continue;
      out[k] = String(r?.value ?? "");
    }
    return out;
  };

  // input_schema → rows of {name, type, example, required, description}
  const schemaToRows = (schema) => {
    const props = (schema && schema.properties) || {};
    const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
    return Object.entries(props).map(([name, def]) => ({
      name,
      type: (def && def.type) || "string",
      example: def && def.example != null ? String(def.example) : "",
      description: (def && def.description) || "",
      required: required.has(name),
    }));
  };

  const rowsToSchema = (rows) => {
    if (!rows || rows.length === 0) return {};
    const properties = {};
    const required = [];
    for (const r of rows) {
      const name = (r?.name || "").trim();
      if (!name) continue;
      const type = r.type || "string";
      const def = { type };
      if (r.description) def.description = r.description;
      if (r.example !== "" && r.example != null) {
        // Cast example by declared type so the JSON schema looks right.
        if (type === "number" || type === "integer") {
          const n = Number(r.example);
          if (!Number.isNaN(n)) def.example = type === "integer" ? Math.trunc(n) : n;
        } else if (type === "boolean") {
          def.example = String(r.example).toLowerCase() === "true";
        } else {
          def.example = r.example;
        }
      }
      properties[name] = def;
      if (r.required) required.push(name);
    }
    const out = { type: "object", properties };
    if (required.length) out.required = required;
    return out;
  };

  const openCreate = () => {
    setEditing(null);
    reset({
      name: "", description: "", endpoint: "",
      method: "POST",
      headers: [{ key: "", value: "" }],
      schema_fields: [],
      timeout_ms: 5000,
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const headerRows = headersToRows(row.headers);
    const schemaRows = schemaToRows(row.input_schema);
    reset({
      name: row.name || "",
      description: row.description || "",
      endpoint: row.endpoint || "",
      method: row.method || "POST",
      headers: headerRows.length ? headerRows : [{ key: "", value: "" }],
      schema_fields: schemaRows,
      timeout_ms: row.timeout_ms || 5000,
      is_active: !!row.is_active,
    });
    setShowModal(true);
  };

  const onSubmit = async (data) => {
    const payload = {
      name: data.name.trim(),
      description: data.description || "",
      endpoint: data.endpoint.trim(),
      method: data.method || "POST",
      headers: rowsToHeaders(data.headers),
      input_schema: rowsToSchema(data.schema_fields),
      timeout_ms: parseInt(data.timeout_ms, 10) || 5000,
      is_active: !!data.is_active,
    };

    setSubmitting(true);
    try {
      if (editing) {
        await CallingV2.updateTool(editing.id, payload);
        triggerAlert("success", "Saved", "Tool updated.");
      } else {
        await CallingV2.createTool(payload);
        triggerAlert("success", "Created", "Tool added.");
      }
      setShowModal(false);
      reset();
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.response?.data && typeof e.response.data === "object" ? Object.values(e.response.data).flat().join(" ") : null)
        || "Save failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSubmitting(false); }
  };

  const toggleStatus = async (row) => {
    try {
      await CallingV2.updateTool(row.id, { is_active: !row.is_active });
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Could not update."); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete tool?",
      message: `Delete tool "${row.name}"?`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteTool(row.id);
      triggerAlert("success", "Deleted", "Tool removed.");
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Delete failed."); }
  };

  const onTest = async (row) => {
    setShowTest({ tool: row, result: null, loading: true });
    try {
      // Try to use the example fields from input_schema (if any) for the payload
      let sample = {};
      try {
        const props = row.input_schema?.properties || {};
        sample = Object.fromEntries(Object.entries(props).map(([k, v]) => [k, (v && v.example) ?? ""]));
      } catch (_e) { sample = {}; }
      const r = await CallingV2.testTool(row.id, sample);
      setShowTest({ tool: row, result: r.data, loading: false });
    } catch (e) {
      setShowTest({ tool: row, result: { ok: false, error: e?.message || "Request failed" }, loading: false });
    }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Name", sortable: true, selector: (r) => r.name, cell: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    {
      name: "Method", sortable: true, selector: (r) => r.method, width: "100px",
      cell: (r) => <span className="badge bg-info">{r.method}</span>,
    },
    {
      name: "Endpoint", sortable: true, selector: (r) => r.endpoint,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{r.endpoint}</span>,
    },
    {
      name: "Timeout", sortable: true, selector: (r) => r.timeout_ms, width: "110px",
      cell: (r) => <span>{r.timeout_ms} ms</span>,
    },
    {
      name: "Status", sortable: true, selector: (r) => r.is_active, width: "110px",
      cell: (r) => r.is_active
        ? <span className="badge bg-success">Active</span>
        : <span className="badge bg-danger">Inactive</span>,
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-primary btn-sm" onClick={() => openEdit(r)}>Edit</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => onTest(r)}>Test</button>
          <button className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`} onClick={() => toggleStatus(r)}>
            {r.is_active ? "Pause" : "Activate"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "#064c77", borderBottom: "1px solid #dee2e6", minHeight: "52px" } },
    headCells: { style: { color: "#ffffff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "60px", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8f9fa" } } },
    cells: { style: { fontSize: "14px", color: "#495057", borderBottom: "1px solid #f1f3f5" } },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Tool Catalog" heading="Calling V2 / Tools" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                Catalog of HTTP tools any V2 agent can call during a conversation
                (e.g., "Book a meeting" → POST to a Calendly webhook). Tools added
                here are visible to every customer's agent edit page.
              </div>
              <div className="row align-items-center mb-3">
                <div className="col-md-8"></div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-primary w-md btn-md" onClick={openCreate}>+ New Tool</button>
                </div>
              </div>
              {!loading ? (
                <DataTable columns={columns} data={rows} pagination fixedHeader fixedHeaderScrollHeight="640px" persistTableHead customStyles={customStyles}
                  noDataComponent={<div style={{ padding: 24 }}>No tools yet.</div>} />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Create/Edit modal */}
      <Modal size="lg" show={showModal} onHide={() => !submitting && setShowModal(false)} backdrop="static" keyboard={!submitting}>
        <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
          <h1 className="modal-title fs-5">{editing ? "Edit Tool" : "New Tool"}</h1>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="row">
              <div className="col-md-8 mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input className="form-control" placeholder='e.g. "create_ticket"' {...register("name", {
                  required: "Required",
                  pattern: {
                    value: /^[a-z][a-z0-9_]*$/,
                    message: 'Use snake_case: lowercase letters, digits, underscores; must start with a letter. No spaces or hyphens.',
                  },
                })} />
                {errors.name
                  ? <div className="text-danger font_14 mt-1">{errors.name.message}</div>
                  : <div className="text-muted font_14 mt-1">snake_case only — passed to the LLM as the function name.</div>
                }
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Method</label>
                <select className="form-select" {...register("method")}>
                  <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
                </select>
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Endpoint <span className="text-danger">*</span></label>
                <input className="form-control" placeholder="https://..." {...register("endpoint", { required: "Required" })} />
                {errors.endpoint && <div className="text-danger font_14 mt-1">{errors.endpoint.message}</div>}
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows="2" placeholder='What does the agent use this for? e.g. "Book a meeting on the company calendar."' {...register("description")} />
              </div>
              {/* ── Headers (key/value rows) ──────────────────────────── */}
              <div className="col-12 mb-3">
                <label className="form-label d-block">HTTP Headers</label>
                <small className="text-muted d-block mb-2">
                  Sent with every request the agent makes to this tool. Common: <code>Authorization</code>, <code>X-Api-Key</code>.
                </small>
                {headerFields.length === 0 && (
                  <div className="text-muted small mb-2">No headers yet.</div>
                )}
                {headerFields.map((f, idx) => (
                  <div key={f.id} className="row g-2 align-items-center mb-2">
                    <div className="col-md-5">
                      <input className="form-control" placeholder="Header name (e.g. Authorization)" {...register(`headers.${idx}.key`)} />
                    </div>
                    <div className="col-md-6">
                      <input className="form-control" placeholder="Header value (e.g. Bearer sk-...)" {...register(`headers.${idx}.value`)} />
                    </div>
                    <div className="col-md-1 text-end">
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeHeader(idx)} title="Remove">×</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => appendHeader({ key: "", value: "" })}>+ Add Header</button>
              </div>

              {/* ── Input schema (field rows) ─────────────────────────── */}
              <div className="col-12 mb-3">
                <label className="form-label d-block">Input Fields (what the agent must collect)</label>
                <small className="text-muted d-block mb-2">
                  Each row becomes one property on the tool's input. Example: a meeting-booking tool typically takes <code>datetime</code> (string), <code>attendee_email</code> (string), <code>duration_minutes</code> (integer).
                </small>
                {schemaFields.length === 0 && (
                  <div className="text-muted small mb-2">No fields. Leave empty if the tool takes no arguments.</div>
                )}
                {schemaFields.map((f, idx) => (
                  <div key={f.id} className="row g-2 align-items-center mb-2">
                    <div className="col-md-3">
                      <input className="form-control" placeholder="Field name (e.g. datetime)" {...register(`schema_fields.${idx}.name`)} />
                    </div>
                    <div className="col-md-2">
                      <select className="form-select" {...register(`schema_fields.${idx}.type`)}>
                        <option value="string">string</option>
                        <option value="integer">integer</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input className="form-control" placeholder="Example (optional)" {...register(`schema_fields.${idx}.example`)} />
                    </div>
                    <div className="col-md-3">
                      <input className="form-control" placeholder="Description (optional)" {...register(`schema_fields.${idx}.description`)} />
                    </div>
                    <div className="col-md-1 text-end d-flex align-items-center gap-1 justify-content-end">
                      <div className="form-check mb-0" title="Required">
                        <input className="form-check-input" type="checkbox" {...register(`schema_fields.${idx}.required`)} />
                      </div>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeSchema(idx)} title="Remove">×</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => appendSchema({ name: "", type: "string", example: "", description: "", required: false })}>+ Add Field</button>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Timeout (ms)</label>
                <input type="number" min="100" max="60000" className="form-control" {...register("timeout_ms")} />
              </div>
              <div className="col-12 form-check ms-2">
                <input className="form-check-input" type="checkbox" id="tool_active" {...register("is_active")} />
                <label htmlFor="tool_active" className="form-check-label">Active</label>
              </div>
            </div>
          </ModalBody>
          <Modal.Footer>
            <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Saving..." : "Save"}</button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Test result modal */}
      <Modal size="lg" show={!!showTest} onHide={() => setShowTest(null)}>
        {showTest && (
          <>
            <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
              <h1 className="modal-title fs-5">Test: {showTest.tool.name}</h1>
            </Modal.Header>
            <ModalBody>
              {showTest.loading ? <Loader /> : (
                <>
                  <p>
                    <strong>Status:</strong>{" "}
                    {showTest.result?.ok
                      ? <span className="badge bg-success">{showTest.result.status_code} OK</span>
                      : <span className="badge bg-danger">{showTest.result?.status_code || "ERROR"}</span>}
                    {" "}<span className="text-muted small">{showTest.result?.elapsed_ms} ms</span>
                  </p>
                  {showTest.result?.error && <div className="alert alert-danger">{showTest.result.error}</div>}
                  {showTest.result?.body && (
                    <>
                      <h6>Response body</h6>
                      <pre style={{ background: "#f8f9fa", padding: 12, borderRadius: 6, maxHeight: 360, overflow: "auto", fontSize: 12 }}>
                        {showTest.result.body}
                      </pre>
                    </>
                  )}
                </>
              )}
            </ModalBody>
            <Modal.Footer>
              <button className="btn btn-primary" onClick={() => setShowTest(null)}>Close</button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}
